/**
 * DownloadResult — the third and last surface (RES-CLIENT-001 §8.1).
 *
 * ## Why a download surface exists at all — measured, not assumed
 *
 * §6 found a LEGITIMATE GAP in the shared factory:
 * `apps/port-web/src/portals/platform-admin/services/platformApi.js` **imports
 * `createApiClient` at `:30` and hand-rolls a `fetch` beside it at `:155`**,
 * re-reading the error envelope by hand at `:174`, purely because the factory had
 * no way to return a binary body. That file is direct evidence that the gap is
 * real; the sibling of it, `apps/purchase-web/src/core/api/apiClient.js`, is NOT
 * (its stated reason — avoiding an axios dependency — expired when the app declared
 * both `axios` and `@puffin-core/api-client`).
 *
 * A download body is **not an envelope**, so `readEnvelopeData` must never touch it.
 * That is the whole reason this is a separate named surface rather than a flag.
 *
 * @module @puffin-core/api-client/facade/downloadResult
 */

import { installDataTripwire } from './dataTripwire.js';

/**
 * Read by the `.data` tripwire.
 *
 * ⚠️ §8.1 specifies the tripwire only for `ApiResult`. It is installed here too, by
 * exactly that section's argument: a caller migrating `res.data` (the blob) to
 * `.body` would otherwise read `undefined` and hand an empty file to
 * `URL.createObjectURL` — a silently CORRUPT download, which is a worse outcome
 * than either a loud throw or a missing member.
 */
const DATA_TRIPWIRE_MESSAGE =
  'DownloadResult has no `data` — the downloaded body is `.body` (RES-CLIENT-001).';

/**
 * Build the frozen `DownloadResult` handed back by every `api.download.<verb>` call.
 *
 * Every key is ALWAYS present; `contentType` / `filename` / `headers` are `null`
 * when the server sent none, never missing.
 *
 * The `fields` argument is REQUIRED — there is no `= {}` default, for the same
 * reason as `createApiResult`: a result built from nothing describes no response.
 *
 * @param {Object} fields
 * @param {*} [fields.body] - The response body, verbatim: Blob, ArrayBuffer, string.
 * @param {string|null} [fields.contentType] - The `Content-Type` header, or null.
 * @param {string|null} [fields.filename] - Parsed from `Content-Disposition`, or null.
 * @param {*} [fields.headers] - The response header container, verbatim.
 * @param {number} [fields.status] - HTTP status.
 * @returns {Readonly<Object>} A frozen DownloadResult.
 */
export function createDownloadResult({ body, contentType, filename, headers, status }) {
  const result = {
    body: body ?? null,
    contentType: contentType ?? null,
    filename: filename ?? null,
    headers: headers ?? null,
    status,
  };

  installDataTripwire(result, DATA_TRIPWIRE_MESSAGE);

  return Object.freeze(result);
}
